<script setup lang="ts">
import { computed } from 'vue'
import { RadioGroupRoot, useForwardProps } from 'radix-vue'
import { cn } from '../../helpers/utils'
import type { ESelectorItemVariants, TSelectorOption } from './types'
import { PSelectorItem } from './parts'

const props = defineProps<{
    class?: string
    options: TSelectorOption[]
    variant: ESelectorItemVariants | `${ESelectorItemVariants}`
    disabled?: boolean
    layout: 'grid' | 'swipe'
}>()

const model = defineModel()

const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
})

const forwarded = useForwardProps(delegatedProps)

const isGroupHasAnyBadge = Boolean(
    !!props.options.find(option => option.badge?.text) && props.options && props.options.length,
)

const numberOfColumns = props.options.length
</script>

<template>
    <RadioGroupRoot
        :style="layout === 'grid' && `grid-template-columns: repeat(${numberOfColumns}, minmax(0, 1fr))`"
        :class="cn(`gap-2`, layout === 'grid' ? 'grid' : 'flex flex-nowrap overflow-x-auto', props.class)"
        v-bind="forwarded"
        tabindex="0"
        data-gpp-tools-ui
    >
        <PSelectorItem
            v-for="option in options"
            :id="option.id"
            :key="option.id"
            :value="option.value"
            :label="option.label"
            :paragraph="option.paragraph"
            :is-selected="modelValue === option.value"
            :variant="props.variant"
            :disabled="props.disabled"
            :badge="option.badge"
            :is-group-has-any-badge="isGroupHasAnyBadge"
            :class="`${layout === 'swipe' && 'layout-swipe shrink-0'}`"
            @update-model="model = option.value"
        >
            <template #badge="{ content }">
                <slot
                    name="items-badge"
                    :content="content"
                />
            </template>
            <template #label="{ text, classes }">
                <slot
                    name="items-label"
                    :text="text"
                    :classes="classes"
                />
            </template>
            <template #indicator="{ classes }">
                <slot
                    name="items-indicator"
                    :classes="classes"
                />
            </template>
            <template #paragraph="{ text, classes }">
                <slot
                    name="items-paragraph"
                    :text="text"
                    :classes="classes"
                />
            </template>
        </PSelectorItem>
    </RadioGroupRoot>
</template>

<style scoped>
.layout-swipe{width:calc(45.45455% - var(--size-units-2))}
</style>
